/**
 * Utility functions for social media sharing
 */

/**
 * Interface for social share options
 */
export interface SocialShareOptions {
  url: string;
  title?: string;
  description?: string;
  image?: string;
}

/**
 * Share content on Twitter/X
 * @param options Share options
 */
export const shareOnTwitter = (options: SocialShareOptions): string => {
  const { url, title } = options;
  // Combine title and URL for better sharing experience
  const text = title ? `${title} ${url}` : url;
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
};

/**
 * Share content on Facebook
 * @param options Share options
 */
export const shareOnFacebook = (options: SocialShareOptions): string => {
  const { url } = options;
  const shareUrl = encodeURIComponent(url);
  return `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
};

/**
 * Share content on WhatsApp
 * @param options Share options
 */
export const shareOnWhatsApp = (options: SocialShareOptions): string => {
  const { url, title } = options;
  // Format: Title + URL for better sharing experience
  const text = title ? `${title}\n\n${url}` : url;
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
};

/**
 * Share content on LinkedIn
 * @param options Share options
 */
export const shareOnLinkedIn = (options: SocialShareOptions): string => {
  const { url } = options;
  const shareUrl = encodeURIComponent(url);
  // LinkedIn's sharing API only reliably supports the URL parameter
  // The title and description are extracted from the page's metadata
  return `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;
};

/**
 * Share content on Instagram (note: Instagram doesn't support direct URL sharing via web)
 * This function returns a URL that can be used to open Instagram, but the user will need to manually share
 */
export const shareOnInstagram = (): string => {
  return "https://www.instagram.com/";
};

/**
 * Generate all social share links for a blog post
 * @param options Share options
 */
export const generateSocialShareLinks = (options: SocialShareOptions) => {
  return {
    twitter: shareOnTwitter(options),
    facebook: shareOnFacebook(options),
    whatsapp: shareOnWhatsApp(options),
    linkedin: shareOnLinkedIn(options),
    instagram: shareOnInstagram(),
  };
};

/**
 * Copy URL to clipboard
 * @param url URL to copy
 * @returns Promise that resolves when the URL is copied
 */
export const copyToClipboard = async (url: string): Promise<boolean> => {
  // Check if navigator is available (client-side only)
  if (typeof navigator === "undefined" || !navigator.clipboard) {
    console.error("Clipboard API not available");
    return false;
  }

  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (error) {
    console.error("Failed to copy URL to clipboard:", error);
    return false;
  }
};
